/**
 * usePersonalLibrary
 * ──────────────────
 * Hook + helper for the Personal Image Library — the central repo of
 * personalised images the user collects/generates throughout the app.
 *
 * `addToLibrary(url, opts)` — fire-and-forget POST to /api/personal-library/add
 * `useLibrary()`            — returns { items, loading, refresh, remove }
 */
import { useEffect, useState, useCallback } from 'react';
import { useResolvedUserId } from './useResolvedUserId';

const API = process.env.REACT_APP_BACKEND_URL;

/** Add an image to the user's personal library. Resolves userId from auth. */
export const addToLibrary = (userId, image_url, { label, source = 'unknown', metadata } = {}) => {
  if (!userId || !image_url) return Promise.resolve(null);
  return fetch(`${API}/api/personal-library/add`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, image_url, label, source, metadata }),
  })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
};

export const useLibrary = () => {
  const userId = useResolvedUserId();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    if (!userId) return;
    setLoading(true);
    fetch(`${API}/api/personal-library/${encodeURIComponent(userId)}`)
      .then((r) => r.json())
      .then((d) => setItems(d?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => { refresh(); }, [refresh]);

  const remove = useCallback(
    (itemId) =>
      fetch(`${API}/api/personal-library/${encodeURIComponent(userId)}/${itemId}`, { method: 'DELETE' })
        .then(() => setItems((xs) => xs.filter((i) => i.id !== itemId)))
        .catch(() => {}),
    [userId],
  );

  return { userId, items, loading, refresh, remove };
};
